import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { useImmer } from 'use-immer'
import { IMessage } from './interfaces/IMessages'
import { createChatBotMessage, createClientMessage, sleep } from './utilities'
import { STREAMING } from '../../../constants'

interface ContextProps {
  chatMessages: IMessage[]
  addUserMessage: (message: string) => void
  addBotMessage: (message: string, messageId: string, delay?: number) => void
  clearBotMessage: (messageId: string) => void
  addLoadingMessage: () => void
  isLoading: boolean
  setIsLoading: any
  isRecording: boolean
  setIsRecording: any
}

const ChatContext = createContext<ContextProps>({
  chatMessages: [],
  addUserMessage: (message: string) => undefined,
  addBotMessage: (message: string, messageId: string, delay?: number) => undefined,
  clearBotMessage: (messageId: string) => undefined,
  addLoadingMessage: () => undefined,
  isLoading: false,
  setIsLoading: () => undefined,
  isRecording: true,
  setIsRecording: () => undefined,
})

export const useChatbot = () => useContext(ChatContext)

export const ChatbotProvider = ({ children }: any) => {
  const [chatMessages, setChatMessages] = useImmer<IMessage[]>([])
  const messageQueue: IMessage[] = useMemo(() => [], [])
  const [isLoading, setIsLoading] = useImmer(false)
  const [isRecording, setIsRecording] = useImmer(true)

  const addDelayedBotMessage = async (message: string, messageId: string, delay: number) => {
    await sleep(delay)
    if (STREAMING) {
      const tokens = message.match(/.{1,5}/g) || []
      for (const messageToken of tokens) {
        setChatMessages((draft) => {
          const existingMessageIndex = draft.findIndex((msg) => msg.payload?.messageId === messageId)
          if (existingMessageIndex !== -1) draft[existingMessageIndex].message += messageToken
          else draft.push(createChatBotMessage(messageToken, { payload: { messageId } }))
        })
        await sleep(100)
      }
    } else {
      setChatMessages((draft) => {
        const existingMessageIndex = draft.findIndex((msg) => msg.payload?.messageId === messageId)
        if (existingMessageIndex !== -1) draft[existingMessageIndex].message += ' ' + message
        else draft.push(createChatBotMessage(message, { payload: { messageId } }))
      })
    }
    console.debug(`Added BotMessage: ${message}`)
  }

  const processMessageQueue = async () => {
    while (messageQueue.length > 0) {
      const currentMessage = messageQueue[0]
      const correctedDelay = Math.max(0, (currentMessage.delay ?? 0) - Date.now())
      await addDelayedBotMessage(currentMessage.message, String(currentMessage.id), correctedDelay)
      messageQueue.shift()
    }
  }

  const addBotMessage = (message: string, id: string, delay?: number) => {
    if (!message || message.trim() == '') return
    messageQueue.push({ message, type: 'bot', id, delay, payload: { messageId: id } })
    if (messageQueue.length === 1) processMessageQueue()
  }

  const addUserMessage = useCallback((message: string) => {
    setChatMessages((draft) => {
      draft.push(createClientMessage(message, {}))
    })
    // Set loading to true immediately after adding the user message
    setIsLoading(true)
    console.debug(`Added UserMessage: ${message}`)
  }, [])

  const clearBotMessage = useCallback((messageId: string) => {
    setChatMessages((draft) => {
      const messageIndex = draft.findIndex((msg) => msg.payload?.messageId === messageId)
      if (messageIndex !== -1) draft[messageIndex].message = ''
    })
    console.debug(`Cleared BotMessage with id: ${messageId}`)
  }, [])

  const addLoadingMessage = useCallback(() => {
    setChatMessages((draft) => draft.filter((msg) => msg.message).push(createChatBotMessage('. . .', { loading: true })))
    console.debug(`Added LoadingMessage`)
  }, [])

  return (
    <ChatContext.Provider
      value={{
        chatMessages,
        addUserMessage,
        addBotMessage,
        clearBotMessage,
        addLoadingMessage,
        isLoading,
        setIsLoading,
        isRecording,
        setIsRecording,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}
